import { Typography } from '@mui/joy';
import { get } from 'lodash';
import { useEffect, useRef } from 'react';
import { FieldError, UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form } from 'core/components';
import { Loader } from 'core/elements';
import { useFormContext, useNotificationContext } from 'core/states';
import { useSavePartParticulars } from 'parts/hooks';
import { IPartFieldConfig } from 'parts/types';
import { Color, Level, Size } from 'theme/enums';

import { FieldsBody } from './FieldsBody';
import { buildSavePayload } from './utils';

interface IProps {
  allConfigs: IPartFieldConfig[];
  defaultValues: Record<string, unknown>;
  fieldNameMap: Map<string, number>;
  groupedConfigsPerColumn: Array<Array<[string, IPartFieldConfig[]]>>;
  isError: boolean;
  isLoading: boolean;
  isOpen: boolean;
  manufacturerId?: number | null;
  onClose: () => void;
  partNumber: string;
}

export const EditPartParticularsFormFields = ({
  allConfigs,
  defaultValues,
  fieldNameMap,
  groupedConfigsPerColumn,
  isError,
  isLoading,
  isOpen,
  manufacturerId,
  onClose,
  partNumber,
}: IProps) => {
  const { t } = useTranslation();
  const { setError, clearErrors, errors, isSubmitting, watch } = useFormContext();
  const { clearNotification } = useNotificationContext();
  const { savePartParticulars } = useSavePartParticulars();

  const errorsRef = useRef(errors);
  errorsRef.current = errors;

  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current != null) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (!name) {
        return;
      }
      const fieldError = get(errorsRef.current, name) as unknown as FieldError | undefined;

      if (fieldError?.type === 'server') {
        clearErrors(name);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  useEffect(() => {
    return () => clearCloseTimeout();
  }, []);

  const handleOnClose = (reset?: UseFormReset<Record<string, unknown>>) => {
    clearCloseTimeout();
    clearNotification();
    onClose();
    reset?.(defaultValues);
  };

  const onSubmit =
    (reset: UseFormReset<Record<string, unknown>>) =>
    async (formValues: Record<string, unknown>) => {
      if (manufacturerId == null) {
        return false;
      }

      const response = await savePartParticulars(
        buildSavePayload(partNumber, manufacturerId, allConfigs, formValues)
      );

      if (!response?.success) {
        const messagesByFieldId = new Map<number, Set<string>>();

        response?.errors?.forEach(({ field, message }) => {
          const fieldId = fieldNameMap.get(field);

          if (fieldId == null) {
            console.warn(`Edit Particulars: unmapped server error field "${field}" — ${message}`);

            return;
          }

          const messages = messagesByFieldId.get(fieldId) ?? new Set<string>();

          messages.add(message);
          messagesByFieldId.set(fieldId, messages);
        });

        messagesByFieldId.forEach((messages, fieldId) => {
          setError(String(fieldId), { type: 'server', message: [...messages].join(' • ') });
        });

        return false;
      }

      clearCloseTimeout();
      closeTimeoutRef.current = setTimeout(() => handleOnClose(reset), 1000);
    };

  return (
    <Form
      title={t('titleEditParticulars')}
      isOpen={isOpen}
      onClose={handleOnClose}
      onSubmit={onSubmit}
      submitBtnLabel={t('buttonLabelSave')}
      cancelBtnLabel={t('buttonLabelCancel')}
      disabledSubmit={isSubmitting || isError || allConfigs.length === 0}
      level={1}
      size={Size.LG}
      shouldSubmitOnKeyDown={false}
    >
      {isLoading && <Loader />}
      {!isLoading && isError && (
        <Typography level={Level.TITLE_SM} color={Color.DANGER}>
          {t('errorTitlePartFieldConfigsFetch')}
        </Typography>
      )}
      {!isLoading && !isError && allConfigs.length > 0 && (
        <FieldsBody
          groupedConfigsPerColumn={groupedConfigsPerColumn}
          detailsLabel={t('labelDetails')}
        />
      )}
    </Form>
  );
};
